import { defineComponent as _defineComponent } from 'vue'
import {
	ObjectType,
	Platform,
	SortOrder,
	SportCompetitorV2,
	SportEventMatrixCompetitionColumn,
	SportEventStatus,
	SportEventV2,
	SportType,
} from '@/@types/graphql-types';
import Breadcrumbs from '@/features/sports/components/ui/Breadcrumbs.vue';
import CompetitorLogo from '@/features/sports/components/ui/CompetitorLogo.vue';
import Header from '@/features/sports/components/ui/Header.vue';
import SingleStepEventEncounter from '@/features/sports/components/global/SingleStepEventEncounter.vue';
import SingleStepEventEncounterList from '@/features/sports/components/global/SingleStepEventEncounterList.vue';
import SportsLayout from '@/features/sports/components/global/SportsLayout.vue';
import EventEncounterLarge from '@/features/sports/components/team/EventEncounterLarge.vue';
import TeamCompetitionsCalendar from '@/features/sports/components/team/TeamCompetitionsCalendar.vue';
import { useQuery } from '@/helpers/composables/useApollo';
import { useI18n } from '@/helpers/composables/useI18n';
import { useSportEvents } from '@/features/sports/composables/useSportEvents';
import { useLanguageStore, useMetaStore, useRoutingStore } from '@/helpers/composables/useStores';
import { shiftDateWithTimezone } from '@/helpers/date-helper';
import { getSportPageContext } from '@/features/sports/helpers/config';
import { SnowplowContext, SnowplowSportContext } from '@/helpers/tracking/providers';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { BreadcrumbItem, SportPageType } from '@/features/sports/types/interfaces';
import {
	GetSingleStepTeamDocument,
	GetSingleStepTeamQuery,
	GetSingleStepTeamQueryVariables,
} from '@/features/sports/graphql/queries/GetSingleStepTeam.query';
import { computed, onActivated, ref } from 'vue';
import { Route } from 'vue-router';


export default /*#__PURE__*/_defineComponent({
  __name: 'SingleStepTeam',
  props: {
    lockedRoute: null
  },
  setup(__props: any) {

const props = __props as {
	lockedRoute: Route;
};

const languageStore = useLanguageStore();
const metaStore = useMetaStore();
const routingStore = useRoutingStore();
const { t, tc } = useI18n();

const selectedCompetitionsIds = ref<string[]>([]);
const selectedProviderShortnames = ref<string[]>([]);
const competitionColumns = ref<SportEventMatrixCompetitionColumn[]>([]);



const queryVariables = computed<GetSingleStepTeamQueryVariables>(() => ({
	fullPath: props.lockedRoute.path,
	language: languageStore.language.value,
	country: languageStore.country.value,
	site: routingStore.activeDomain.value,
	platform: Platform.Web,
	first: 3, // number of competitions to get events for (competition filtering fetches the others)
	bucketSize: 10,
	timezoneOffset: 0,
	filter: {
		startAt: {
			from: shiftDateWithTimezone(languageStore.localeObject.value.timezone).toISOString(),
		},
		packages: selectedProviderShortnames.value,
		competitionIds: selectedCompetitionsIds.value,
	},
}));
const queryVariablesPast = computed<GetSingleStepTeamQueryVariables>(() => ({
	...queryVariables.value,
	filter: {
		...queryVariables.value.filter,
		startAt: {
			to: shiftDateWithTimezone(languageStore.localeObject.value.timezone).toISOString(),
			// at the moment, from is required for query to work properly.
			// 2023 is an arbitrary date in past that should return past events for all teams, while limiting the amount of data fetched on BE before filtering/limiting
			from: '2023-01-01T00:00:00.000Z',
		},
	},
	sortOrder: SortOrder.Desc,
}));
const queriesEnabled = computed(() => !!(props.lockedRoute.name && props.lockedRoute.name.includes('team')));

const singleStepTeamQuery = useQuery<GetSingleStepTeamQuery, GetSingleStepTeamQueryVariables>(
	GetSingleStepTeamDocument,
	queryVariables,
	{
		fetchPolicy: 'cache-and-network',
		enabled: queriesEnabled.value,
	}
);
singleStepTeamQuery.onResult(({ data }) => updateJsonLd(data?.urlV2.node as SportCompetitorV2));

const pastSingleStepTeamQuery = useQuery<GetSingleStepTeamQuery, GetSingleStepTeamQueryVariables>(
	GetSingleStepTeamDocument,
	queryVariablesPast,
	{
		fetchPolicy: 'cache-and-network',
		enabled: queriesEnabled,
	}
);
pastSingleStepTeamQuery.onResult(({ data }) => updateJsonLd(data?.urlV2.node as SportCompetitorV2));

const isDataLoading = computed(() => !!singleStepTeamQuery.loading.value && !!pastSingleStepTeamQuery.loading.value);
const hasUpcomingEvents = computed(() => {
	const node = singleStepTeamQuery.result.value?.urlV2.node as SportCompetitorV2;
	if ((node?.sportEventBucketsV2.edges?.length || 0) === 0) {
		return false;
	}

	// we assume there are upcoming events before any requests have been done
	return true;
});
const singleStepTeam = computed<SportCompetitorV2 | undefined>(() => {
	if (isDataLoading.value) {
		return undefined;
	}

	return hasUpcomingEvents.value
		? (singleStepTeamQuery.result.value?.urlV2.node as SportCompetitorV2)
		: (pastSingleStepTeamQuery.result.value?.urlV2.node as SportCompetitorV2);
});

const competitionEvents = computed(() => singleStepTeam.value?.sportEventBucketsV2?.edges ?? []);
const teamName = computed(() => singleStepTeam.value?.content?.name ?? '');
const listHeading = computed(() =>
	!hasUpcomingEvents.value ? tc('SPORTS_WEBAPP_LIVE_N_PAST_EVENTS') : tc('SPORTS_WEBAPP_LIVE_N_UPCOMING_EVENTS')
);

const context = computed(() =>
	getSportPageContext({
		sport: props.lockedRoute.meta?.sport! as SportType,
		pageType: SportPageType.SportTeam,
	})
);

// Extract events from the already-fetched team query data to prevent query waterfall and CLS
const providedEventsFromQuery = computed(() => {
	// Try upcoming query first (most common case and matches parent query)
	const upcomingEvents = extractEventsFromTeam(singleStepTeamQuery.result.value?.urlV2.node as SportCompetitorV2);
	if (upcomingEvents.length > 0) {
		return upcomingEvents;
	}

	// Fall back to past events query if upcoming has no events
	const pastEvents = extractEventsFromTeam(
		pastSingleStepTeamQuery.result.value?.urlV2.node as SportCompetitorV2 as SportCompetitorV2
	);
	if (pastEvents.length > 0) {
		return pastEvents;
	}

	return null;
});

const { sportEvents, events, eventIs } = useSportEvents({
	context,
	competitorId: () => singleStepTeam.value?.id ?? null,
	allowPastEvents: () => !hasUpcomingEvents.value,
	providedEvents: providedEventsFromQuery,
});

const breadcrumbs = computed<BreadcrumbItem[]>(() => [
	// e.g. Sport
	{ name: tc('WEBAPP_SPORTS'), routeName: 'app.sports.all' },

	// e.g. Fussball
	{
		name: tc(context.value?.sportName || ''),
		objectType: SportPageType.SportTeam,
		url: context.value?.getOverviewUrl(languageStore.webLocale.value),
	},

	// e.g. Bayern Munich
	{
		name: teamName.value,
		objectType: ObjectType.SportCompetition,
		objectId: singleStepTeam.value?.objectId,
		url: props.lockedRoute.fullPath,
	},
]);

const schemaCompetitions = computed(() => {
	const competitionNames = (singleStepTeam.value?.sportEventBucketsV2.edges || []).map(
		comp => comp.key.competition?.content.name
	);
	return competitionNames.map(competitionName => {
		return {
			'@type': 'SportsOrganization',
			name: competitionName,
		};
	});
});
const schemaEvents = computed(() => {
	return (singleStepTeam.value?.sportEventBucketsV2?.edges || [])
		.map(comp => {
			return (comp.node.edges || []).map((event: any) => {
				const eventData = event.node;

				const { key, params } = context.value?.getEventTitle(eventData, languageStore.webLocale.value)!;
				const eventTitle = t(key, params);

				const prices =
					eventData?.offers?.map((offer: any) => offer.retailPrice as number).filter((v: any) => v != null) ??
					[];

				let eventStatus = '';
				switch (eventData.content?.status) {
					case SportEventStatus.Postponed:
						eventStatus = 'EventPostponed';
						break;
					case SportEventStatus.Cancelled:
						eventStatus = 'EventCancelled';
						break;
					default:
						eventStatus = 'EventScheduled';
				}

				return {
					'@context': 'https://schema.org',
					'@type': 'BroadcastEvent',
					name: `${eventTitle} ${eventData?.competition?.content.name} ${
						eventData.content?.season?.name ?? ''
					}`,
					isLiveBroadcast: (eventData?.offers ?? []).length > 0,
					startDate: eventData.content?.startAt ?? '',
					endDate: eventData.content?.endAt ?? '',
					broadcastOfEvent: {
						'@type': 'SportsEvent',
						sport: context.value?.sportType.toLowerCase().replace(/_/g, ' '),
						organizer: {
							'@type': 'Organization',
							name: eventData?.competition?.content.name ?? '',
						},
						eventStatus,
						name: `${eventTitle} ${eventData?.competition?.content.name} ${
							eventData.content?.season?.name ?? ''
						}`,
						awayTeam: {
							'@type': 'SportsTeam',
							name:
								eventData?.competitors?.awayTeam?.competitors
									.map((c: any) => c.content.name)
									.find(Boolean) ?? '',
							sport: context.value?.sportType.toLowerCase().replace(/_/g, ' '),
						},
						homeTeam: {
							'@type': 'SportsTeam',
							name:
								eventData?.competitors?.homeTeam?.competitors
									.map((c: any) => c.content.name)
									.find(Boolean) ?? '',
							sport: context.value?.sportType.toLowerCase().replace(/_/g, ' '),
						},
						startDate: eventData.content?.startAt ?? '',
						endDate: eventData.content?.endAt ?? '',
						location: {
							'@type': 'Place',
							name: eventData.content?.venue?.name,
						},
						offers: eventData?.offers
							? {
									'@type': 'AggregateOffer',
									highPrice: Math.max(...prices) ?? undefined,
									lowPrice: Math.min(...prices) ?? undefined,
									priceCurrency: eventData?.offers.map((offer: any) => offer.currency).find(Boolean),
									offerCount: eventData?.offers.length,
									offers: eventData?.offers.map((offer: any) => ({
										'@type': 'Offer',
										name: `${eventTitle} ${offer.presentationType} ${offer.monetizationType}`,
										price: offer.retailPrice,
										priceCurrency: offer.currency,
										url: offer.standardWebURL,
									})),
							  }
							: null,
						audience: {
							'@type': 'Audience',
							name: 'Sports Fans',
						},
					},
				};
			});
		})
		.flat();
});

const schemaData = computed(() => {
	const schemaDataObject = {
		'@context': 'https://schema.org',
		'@type': 'SportsTeam',
		name: teamName.value,
		sport: context.value?.sportType.toLowerCase().replace(/_/g, ' '),
		memberOf: schemaCompetitions.value,
		events: schemaEvents.value,
	};

	return JSON.parse(JSON.stringify(schemaDataObject).replace(/&quot;/g, '"')) || '';
});

function updateJsonLd(node: SportCompetitorV2) {
	if (node?.sportEventBucketsV2?.edges?.length) {
		metaStore.setJsonLd(schemaData.value);
	}
}

function extractEventsFromTeam(team: SportCompetitorV2 | undefined) {
	return (team?.sportEventBucketsV2.edges ?? []).flatMap(bucket => (bucket.node.edges ?? []).map(edge => edge.node));
}

// TRACKING
const sportContext = computed(() => {
	const ctx = new SnowplowSportContext(context.value?.sportType!, ObjectType.SportCompetitor);
	ctx.competitorIdHome = singleStepTeam.value?.objectId.toString();
	return ctx;
});

function trackCompetitionFilter({ target }: Event) {
	const { value, checked } = target as HTMLInputElement;
	_trackCompetitionFilter(value, checked, [sportContext.value]);
}

function trackSeeAllToggle() {
	_trackSeeAllToggle([sportContext.value]);
}

/* TRACKING SPECS */
// Competition Filter Chips
function _trackCompetitionFilter(competitionId: string, toggleStatus: boolean, contexts: SnowplowContext[]) {
	TrackingHelper.trackEvent(
		'userinteraction',
		{
			action: 'filter_toggled',
			label: 'competition',
			property: competitionId,
			value: toggleStatus ? 1 : 0,
			nonInteraction: false,
		},
		contexts
	);
}

// Provider Filter Chips
function _trackSeeAllToggle(contexts: SnowplowContext[]) {
	TrackingHelper.trackEvent(
		'userinteraction',
		{
			action: 'filter_popover_toggled',
			label: 'WEBAPP_COMPETITORS',
			property: 'sports',
			nonInteraction: false,
		},
		contexts
	);
}

onActivated(() => {
	// note: all sport pages are excluded from automatic page tracking
	/**
	 * setTimeout added here to prevent pageView to be triggered before link click track event.
	 * that's because when adding the @click.native to router-link the track event will get triggered
	 * after the navigation which causes the pageView to get triggered first.
	 *
	 */
	setTimeout(() => {
		TrackingHelper.trackPage(props.lockedRoute.path, [sportContext.value]);
	});
});

return { __sfc: true,languageStore, metaStore, routingStore, t, tc, selectedCompetitionsIds, selectedProviderShortnames, competitionColumns, props, queryVariables, queryVariablesPast, queriesEnabled, singleStepTeamQuery, pastSingleStepTeamQuery, isDataLoading, hasUpcomingEvents, singleStepTeam, competitionEvents, teamName, listHeading, context, providedEventsFromQuery, sportEvents, events, eventIs, breadcrumbs, schemaCompetitions, schemaEvents, schemaData, updateJsonLd, extractEventsFromTeam, sportContext, trackCompetitionFilter, trackSeeAllToggle, _trackCompetitionFilter, _trackSeeAllToggle, Breadcrumbs, CompetitorLogo, Header, SingleStepEventEncounter, SingleStepEventEncounterList, SportsLayout, EventEncounterLarge, TeamCompetitionsCalendar }
}

})